/**
 * Stock containment block on the NCR create form (customer review 2026-07).
 *
 * "Contain stock: Request from supplier" checkbox — when ticked, shows the current
 * stock snapshot pulled from the ERP feed (read-only; dash when the tenant
 * has no stock feed) and the mandatory OK / NOT OK quantities the customer
 * must enter before submitting the claim. The requested/confirmed
 * timestamps captured server-side feed the avg-days-to-contain KPI.
 */
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { PackageCheck } from 'lucide-react';

interface NCRContainmentSectionProps {
  containmentRequired: boolean;
  qtyOk: string;
  qtyNotOk: string;
  /** ERP current-stock snapshot; null = no stock feed / part not found. */
  currentStock: number | null;
  /** Manually-entered current stock, used when the ERP feed has no value. */
  manualStock: string;
  onChange: (
    field: 'containment_required' | 'containment_qty_ok' | 'containment_qty_not_ok' | 'containment_current_stock',
    value: boolean | string,
  ) => void;
  /** CR-6387: backend validation errors for the containment quantities. */
  errorOk?: string;
  errorNotOk?: string;
}

export default function NCRContainmentSection({
  containmentRequired, qtyOk, qtyNotOk, currentStock, manualStock, onChange, errorOk, errorNotOk,
}: NCRContainmentSectionProps) {
  // Tenants without an ERP stock feed always get null here, so the field would
  // sit empty with no way to record the figure. Fall back to manual entry.
  const hasErpStock = currentStock !== null;
  return (
    <div className="border-t pt-4 mt-2">
      <h4 className="text-sm font-semibold mb-1 flex items-center gap-2">
        <PackageCheck className="h-4 w-4 text-emerald-600" /> Stock Containment
      </h4>
      <p className="text-xs text-muted-foreground mb-3">
        Ask the supplier to contain stock for this issue. OK / NOT OK quantities are mandatory when requested.
      </p>
      {/* CR-8715: a checkbox, not a mandatory Yes/No that could never be
          empty. Its one-line heading matches the other labels, so the box sits
          level with the inputs beside it instead of below them. */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 max-w-3xl">
        <div>
          {/* Inline like the <Label>s beside it, so it takes the same line height. */}
          <span className="text-sm font-medium leading-none">Contain stock</span>
          <div className="flex items-center gap-2 h-10">
            <Checkbox
              id="containment_required"
              checked={containmentRequired}
              onCheckedChange={(checked) => onChange('containment_required', checked === true)}
            />
            <Label htmlFor="containment_required" className="font-normal cursor-pointer">Request from supplier</Label>
          </div>
        </div>
        {containmentRequired && (
          <>
            <div>
              <Label htmlFor="containment_current_stock">Current Stock</Label>
              {hasErpStock ? (
                <Input
                  id="containment_current_stock"
                  value={String(currentStock)}
                  readOnly
                  disabled
                  title="Current stock quantity pulled from the ERP (read-only snapshot)"
                />
              ) : (
                <Input
                  id="containment_current_stock"
                  type="number"
                  min="0"
                  value={manualStock}
                  onChange={(e) => onChange('containment_current_stock', e.target.value)}
                  placeholder="Enter current stock"
                  title="No ERP stock feed for this part — enter the current stock manually"
                />
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {hasErpStock ? 'From ERP feed' : 'No ERP feed — enter manually'}
              </p>
            </div>
            <div>
              <Label htmlFor="containment_qty_ok">OK Qty <span className="text-red-500">*</span></Label>
              <Input
                id="containment_qty_ok"
                type="number"
                min="0"
                value={qtyOk}
                onChange={(e) => onChange('containment_qty_ok', e.target.value)}
                placeholder="Checked OK"
                className={errorOk ? 'border-red-500' : undefined}
                aria-invalid={!!errorOk || undefined}
                aria-describedby={errorOk ? 'containment_qty_ok-error' : undefined}
              />
              {errorOk && <p id="containment_qty_ok-error" role="alert" className="text-xs text-red-600 mt-1">{errorOk}</p>}
            </div>
            <div>
              <Label htmlFor="containment_qty_not_ok">NOT OK Qty <span className="text-red-500">*</span></Label>
              <Input
                id="containment_qty_not_ok"
                type="number"
                min="0"
                value={qtyNotOk}
                onChange={(e) => onChange('containment_qty_not_ok', e.target.value)}
                placeholder="Checked NOT OK"
                className={errorNotOk ? 'border-red-500' : undefined}
                aria-invalid={!!errorNotOk || undefined}
                aria-describedby={errorNotOk ? 'containment_qty_not_ok-error' : undefined}
              />
              {errorNotOk && <p id="containment_qty_not_ok-error" role="alert" className="text-xs text-red-600 mt-1">{errorNotOk}</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
